import { Tabs, Tab, TabPanel, TabId, Button } from '@blueprintjs/core';
import { useBankPanelData } from '../hooks/useBankPanelData';
import {
  useApplicationDetail,
  useCreateLoanQuote,
  usePatchLoanQuote,
} from '@axo/shared/data-access/hooks';
import { BankPanels } from '../BankPanels/BankPanels';
import styles from './BankPanelTabs.module.css';
import { SARRInformationBar } from './SARRInformationBar.tsx';
import { useState } from 'react';
import { useBankPanelStore } from '../../../store/bankPanelStore.ts';
import { useIsDesktop } from '../../../hooks/utils/useMediaQuery.ts';
import { useFormContext } from 'react-hook-form';
import { LoanQuoteDataFormValues } from '../QuoteDataForm/QuoteDataFormSchema.ts';

interface IRequestHistoric {
  applicationID: string;
}
const parentID = 'BankPanelTabs';

export function BankPanelTabs({ applicationID }: IRequestHistoric) {
  const { bankPanelData, isLoading } = useBankPanelData(applicationID);
  const application = useApplicationDetail(applicationID);
  const [selectedTabId, setSelectedTabId] = useState<TabId>(
    bankPanelData.allRegistrations.name,
  );

  const {
    isEditingAny,
    getQuotesBeingEdited,
    getQuotesBeingCreated,
    setCancelAll,
  } = useBankPanelStore(applicationID);

  const { mutateAsync: patchLoanQuote, isLoading: isPatchLoanQuoteLoading } =
    usePatchLoanQuote();
  const { mutateAsync: createLoanQuote, isLoading: isCreateLoanQuoteLoading } =
    useCreateLoanQuote();

  const isDesktop = useIsDesktop();

  const handleDiscardChanges = () => {
    setCancelAll();
  };

  const { getValues } = useFormContext<LoanQuoteDataFormValues>();

  const handleSubmitAll = async () => {
    const quotesBeingCreated = getQuotesBeingCreated();
    const quotesBeingEdited = getQuotesBeingEdited();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const quotesToSubmit: Partial<any>[] = [];

    Object.entries(getValues()).map(([key, quote]) => {
      if (quotesBeingCreated.has(key)) {
        quotesToSubmit.push({
          ...quote,
          create: true,
        });
      }
      if (quotesBeingEdited.has(key)) {
        quotesToSubmit.push({
          ...quote,
        });
      }
    });
    try {
      const mutations = quotesToSubmit.map((quote) =>
        quote.create
          ? createLoanQuote({
              applicationID,
              data: {
                Amount: parseFloat(quote?.Amount) ?? 0,
                Duration: parseFloat(quote?.Duration) ?? 0,
                NominalRate: parseFloat(quote?.NominalRate) ?? 0,
                EffectiveRate: parseFloat(quote?.EffectiveRate) ?? 0,
                AnnualRate: parseFloat(quote?.AnnualRate) ?? 0,
                MonthlyPayment: parseFloat(quote?.MonthlyPayment) ?? 0,
                StartupFee: parseFloat(quote?.StartupFee) ?? 0,
                AdministrationFee: parseFloat(quote?.AdministrationFee) ?? 0,
                RefinanceAmount: parseFloat(quote?.RefinanceAmount) ?? 0,
                TopupAmount: parseFloat(quote?.TopupAmount) || 0,
                DisbursedAt: quote?.DisbursedAt || null,
                Status: quote?.Status || '',
                MarketCountry: application.data?.MarketCountry,
                LoanRequestID: quote?.LoanRequestID || '',
                LenderID: quote?.LenderID || '',
                LenderReference: quote?.LenderReference || '',
                LoanApplicationID: applicationID,
              },
            })
          : patchLoanQuote({
              loanQuoteID: quote.ID,
              applicationID,
              data: {
                Amount: parseFloat(quote?.Amount) ?? 0,
                Duration: parseFloat(quote?.Duration) ?? 0,
                NominalRate: parseFloat(quote?.NominalRate) ?? 0,
                AnnualRate: parseFloat(quote?.AnnualRate) ?? 0,
                EffectiveRate: parseFloat(quote?.EffectiveRate) ?? 0,
                MonthlyPayment: parseFloat(quote?.MonthlyPayment) ?? 0,
                StartupFee: parseFloat(quote?.StartupFee) ?? 0,
                AdministrationFee: parseFloat(quote?.AdministrationFee) ?? 0,
                RefinanceAmount: parseFloat(quote?.RefinanceAmount) ?? 0,
                TopupAmount: parseFloat(quote?.TopupAmount) || 0,
                DisbursedAt: quote?.DisbursedAt || null,
                Status: quote?.Status || '',
                MarketCountry: application.data?.MarketCountry,
              },
            }),
      );
      await Promise.all(mutations);
    } catch (e) {
      console.log(e);
    } finally {
      setCancelAll();
    }
  };

  return (
    <>
      {application?.data && (
        <>
          <Tabs
            id={parentID}
            className={styles.tabs}
            onChange={setSelectedTabId}
            selectedTabId={selectedTabId}
          >
            <Tab
              id={bankPanelData.allRegistrations.name}
              title={`${bankPanelData.allRegistrations.name} ${bankPanelData.allRegistrations.data.length}`}
              icon="align-justify"
            />
            <Tab
              id={bankPanelData.offers.name}
              title={`${bankPanelData.offers.name} ${bankPanelData.offers.data.length}`}
              icon="flag"
            />
            <Tab
              id={bankPanelData.declined.name}
              title={`${bankPanelData.declined.name} ${bankPanelData.declined.data.length}`}
              icon="warning-sign"
            />
            <Tab
              id={bankPanelData.pending.name}
              title={`${bankPanelData.pending.name} ${bankPanelData.pending.data.length}`}
              icon="history"
            />
            <Tab
              id={bankPanelData.notRegistered.name}
              title={`${bankPanelData.notRegistered.name} ${bankPanelData.notRegistered.data.length}`}
              icon="graph-remove"
            />
            <Tab
              id={bankPanelData.closed.name}
              title={`${bankPanelData.closed.name} ${bankPanelData.closed.data.length}`}
              icon="disable"
            />
            <div className={styles.tabsActions}>
              {isEditingAny ? (
                <>
                  {isDesktop ? (
                    <>
                      <Button
                        icon="cross"
                        intent="primary"
                        text="Discard changes"
                        variant="minimal"
                        type="button"
                        className={styles.discardButton}
                        onClick={handleDiscardChanges}
                        disabled={
                          isPatchLoanQuoteLoading || isCreateLoanQuoteLoading
                        }
                      />
                      <Button
                        icon="saved"
                        intent="primary"
                        text="Save changes"
                        type="button"
                        onClick={handleSubmitAll}
                        disabled={
                          isPatchLoanQuoteLoading || isCreateLoanQuoteLoading
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        icon="cross"
                        intent="primary"
                        size="small"
                        variant="minimal"
                        type="button"
                        className={styles.discardButton}
                        onClick={handleDiscardChanges}
                        disabled={
                          isPatchLoanQuoteLoading || isCreateLoanQuoteLoading
                        }
                      />
                      <Button
                        icon="saved"
                        intent="primary"
                        size="small"
                        type="button"
                        onClick={handleSubmitAll}
                        disabled={
                          isPatchLoanQuoteLoading || isCreateLoanQuoteLoading
                        }
                      />
                    </>
                  )}
                </>
              ) : null}
            </div>
          </Tabs>
          <SARRInformationBar applicationID={applicationID} />
          <TabPanel
            id={bankPanelData.allRegistrations.name}
            selectedTabId={selectedTabId}
            parentId={parentID}
            className={styles.tabPanel}
            panel={
              <BankPanels.AllRegistrations
                application={application?.data}
                data={bankPanelData.allRegistrations.data}
                isLoading={isLoading}
              />
            }
          />
          <TabPanel
            selectedTabId={selectedTabId}
            parentId={parentID}
            id={bankPanelData.offers.name}
            className={styles.tabPanel}
            panel={
              <BankPanels.Offers
                application={application?.data}
                data={bankPanelData.offers.data}
                isLoading={isLoading}
              />
            }
          />
          <TabPanel
            selectedTabId={selectedTabId}
            parentId={parentID}
            id={bankPanelData.declined.name}
            className={styles.tabPanel}
            panel={
              <BankPanels.Declined
                application={application?.data}
                data={bankPanelData.declined.data}
                isLoading={isLoading}
              />
            }
          />
          <TabPanel
            selectedTabId={selectedTabId}
            parentId={parentID}
            id={bankPanelData.pending.name}
            className={styles.tabPanel}
            panel={
              <BankPanels.Pending
                application={application?.data}
                data={bankPanelData.pending.data}
                isLoading={isLoading}
              />
            }
          />
          <TabPanel
            selectedTabId={selectedTabId}
            parentId={parentID}
            id={bankPanelData.notRegistered.name}
            className={styles.tabPanel}
            panel={
              <BankPanels.NotRegistered
                application={application?.data}
                data={bankPanelData.notRegistered.data}
                isLoading={isLoading}
              />
            }
          />
          <TabPanel
            selectedTabId={selectedTabId}
            parentId={parentID}
            id={bankPanelData.closed.name}
            className={styles.tabPanel}
            panel={
              <BankPanels.Closed
                application={application?.data}
                data={bankPanelData.closed.data}
                isLoading={isLoading}
              />
            }
          />
        </>
      )}
    </>
  );
}
